import { useState } from 'react';
import type { Answer, GeneratedQuestion } from '../types.js';

interface QuestionsFormProps {
  questions: GeneratedQuestion[];
  /** Shown above the questions — the intro (round 1) or a follow-up transition line. */
  headerMessage?: string;
  /** e.g. "Round 2 of 4" — shown for deep follow-up rounds. */
  roundLabel?: string;
  submitLabel?: string;
  /** Answers already saved server-side, used to pre-fill on resume. */
  initialAnswers?: Answer[];
  onSubmit: (answers: Answer[]) => void;
}

export function QuestionsForm({
  questions,
  headerMessage,
  roundLabel,
  submitLabel = 'Get my assessment →',
  initialAnswers,
  onSubmit,
}: QuestionsFormProps): React.JSX.Element {
  // `answers` is the canonical id → answer-string map used for validation + submit.
  const [answers, setAnswers] = useState<Record<string, string>>(() =>
    Object.fromEntries((initialAnswers ?? []).map((a) => [a.id, a.answer])),
  );
  // For multi-select questions we also track the picked options to drive the checkboxes.
  const [selected, setSelected] = useState<Record<string, string[]>>(() => {
    const saved = new Map((initialAnswers ?? []).map((a) => [a.id, a.answer]));
    const entries = questions
      .filter((q) => q.type === 'multiple_choice' && q.select !== 'single' && saved.has(q.id))
      .map((q) => [q.id, (saved.get(q.id) as string).split(', ').filter(Boolean)] as const);
    return Object.fromEntries(entries);
  });
  const [touched, setTouched] = useState(false);

  function setText(id: string, value: string): void {
    setAnswers((a) => ({ ...a, [id]: value }));
  }

  function toggleOption(id: string, opt: string): void {
    const current = selected[id] ?? [];
    const next = current.includes(opt)
      ? current.filter((o) => o !== opt)
      : [...current, opt];
    setSelected((s) => ({ ...s, [id]: next }));
    setAnswers((a) => ({ ...a, [id]: next.join(', ') }));
  }

  const unanswered = questions.filter((q) => !(answers[q.id] ?? '').trim());

  function handleSubmit(e: React.FormEvent): void {
    e.preventDefault();
    setTouched(true);
    if (unanswered.length > 0) return;
    onSubmit(questions.map((q) => ({ id: q.id, answer: answers[q.id].trim() })));
  }

  return (
    <form className="tbo-form" onSubmit={handleSubmit} noValidate>
      {roundLabel && <p className="tbo-round">{roundLabel}</p>}
      {headerMessage && <p className="tbo-intro">{headerMessage}</p>}

      {questions.map((q, i) => (
        <div className="tbo-question" key={q.id}>
          <span className="tbo-qnum">Question {i + 1}</span>
          <div className="tbo-field">
            <span className="tbo-label">{q.text}</span>
            {renderInput(q, {
              text: answers[q.id] ?? '',
              selected: selected[q.id] ?? [],
              onText: (v) => setText(q.id, v),
              onToggle: (opt) => toggleOption(q.id, opt),
            })}
          </div>
          {q.type === 'multiple_choice' && q.select !== 'single' && (
            <p className="tbo-why">Select all that apply.</p>
          )}
          {q.why_we_ask && <p className="tbo-why">{q.why_we_ask}</p>}
        </div>
      ))}

      {touched && unanswered.length > 0 && (
        <div className="tbo-error">Please answer all {questions.length} questions.</div>
      )}

      <button className="tbo-btn" type="submit">
        {submitLabel}
      </button>
    </form>
  );
}

interface InputHandlers {
  text: string;
  selected: string[];
  onText: (v: string) => void;
  onToggle: (opt: string) => void;
}

function renderInput(q: GeneratedQuestion, h: InputHandlers): React.JSX.Element {
  if (q.type === 'multiple_choice' && q.options?.length) {
    // Single-select (mutually exclusive options) → radios; otherwise multi-select checkboxes.
    if (q.select === 'single') {
      return (
        <div className="tbo-radios">
          {q.options.map((opt) => (
            <label key={opt} className="tbo-radio">
              <input
                type="radio"
                name={q.id}
                value={opt}
                checked={h.text === opt}
                onChange={() => h.onText(opt)}
              />
              {opt}
            </label>
          ))}
        </div>
      );
    }
    return (
      <div className="tbo-checks">
        {q.options.map((opt) => (
          <label key={opt} className="tbo-check">
            <input
              type="checkbox"
              checked={h.selected.includes(opt)}
              onChange={() => h.onToggle(opt)}
            />
            {opt}
          </label>
        ))}
      </div>
    );
  }
  // Scale = single rating.
  if (q.type === 'scale') {
    return (
      <div className="tbo-scale">
        {[1, 2, 3, 4, 5].map((n) => (
          <label key={n} className="tbo-radio">
            <input
              type="radio"
              name={q.id}
              value={String(n)}
              checked={h.text === String(n)}
              onChange={() => h.onText(String(n))}
            />
            {n}
          </label>
        ))}
      </div>
    );
  }
  return <textarea rows={3} value={h.text} onChange={(e) => h.onText(e.target.value)} />;
}
